import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ChevronLeft, Upload, X, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];
const YEARS = Array.from({ length: 9 }, (_, i) => String(2018 + i));

// Charger segment definitions
const CHARGER_SEGMENTS = {
  "Small charger":     { label: "Small Charger",    models: ["54.6V/6A","58.4V/6A","67.2V/6A","69.3V/6A","71.2V/6A","73V/6A","84V/5A"] },
  "Fast charger":      { label: "Fast Charger",     models: ["54.6V/10A","58.4V/10A","67.2V/10A","69.3V/10A","71.2V/10A","73V/10A","84V/10A"] },
  "E rickshaw charger":{ label: "Rickshaw Charger", models: ["54.6V/15A","58.4V/15A","67.2V/15A","69.3V/15A","71.2V/15A","73V/15A","54.6V/20A","58.4V/20A","67.2V/20A","69.3V/20A","71.2V/20A","73V/20A"] },
};
function parseChargerModel(s) {
  if (!s || !s.includes("/")) return { voltage:"", ampere:"" };
  const [v,a] = s.split("/"); return { voltage: v||"", ampere: a||"" };
}

const EMPTY_CHARGER = {
  charger_model: "", voltage: "", ampere: "", body_type: "",
  serial_number: "", mfg_month: "", mfg_year: "", warranty_status: "",
  xp_charger_status: "", service_type: "", repair_details: "",
};

const initial = {
  customer_name: "", phone: "", alternate_phone: "", address: "",
  city: "", state: "", pin_code: "",
  chargers: [{ ...EMPTY_CHARGER }],
  service_type: "", received_date: new Date().toISOString().split("T")[0], fault_description: "", repair_notes: "",
  courier_name: "", dispatch_from: "", dispatch_awb: "", tracking_link: "",
  payment_total: "", payment_paid: "", payment_method: "", payment_notes: "",
};

function Section({ title, children, action }) {
  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-6 mb-4 shadow-sm">
      <div className="flex items-center justify-between mb-4 pb-3 border-b border-gray-100">
        <h3 className="text-xs font-bold uppercase tracking-widest text-gray-400" style={{ fontFamily: "'Outfit', sans-serif" }}>
          {title}
        </h3>
        {action}
      </div>
      {children}
    </div>
  );
}

function F({ label, full = false, children }) {
  return (
    <div className={full ? "md:col-span-2" : ""}>
      <Label className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-1.5 block">{label}</Label>
      {children}
    </div>
  );
}

export default function NewTicket() {
  const [form, setForm] = useState(initial);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // Root-level setters
  const set = (key) => (val) => setForm((f) => ({ ...f, [key]: val }));
  const setE = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  // Per-charger setters
  const setC = (idx, key) => (val) =>
    setForm((f) => ({ ...f, chargers: f.chargers.map((c, i) => i === idx ? { ...c, [key]: val } : c) }));
  const setCE = (idx, key) => (e) =>
    setForm((f) => ({ ...f, chargers: f.chargers.map((c, i) => i === idx ? { ...c, [key]: e.target.value } : c) }));

  const addCharger = () =>
    setForm((f) => ({ ...f, chargers: [...f.chargers, { ...EMPTY_CHARGER }] }));
  const removeCharger = (idx) =>
    setForm((f) => ({ ...f, chargers: f.chargers.filter((_, i) => i !== idx) }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    const rootRequired = ["customer_name", "phone", "address", "received_date"];
    if (rootRequired.some((k) => !form[k])) { toast.error("Please fill in all required fields"); return; }
    if (form.chargers.length === 0) { toast.error("Add at least one charger"); return; }

    setLoading(true);
    try {
      const payload = { ...form };
      if (!showPayment) {
        delete payload.payment_total;
        delete payload.payment_paid;
        delete payload.payment_method;
        delete payload.payment_notes;
      }
      if (payload.payment_total) payload.payment_total = parseFloat(payload.payment_total);
      if (payload.payment_paid) payload.payment_paid = parseFloat(payload.payment_paid);

      const { data: ticket } = await axios.post(`${API}/tickets`, payload, { withCredentials: true });
      for (const file of files) {
        const fd = new FormData(); fd.append("file", file);
        await axios.post(`${API}/tickets/${ticket._id}/upload`, fd, { withCredentials: true });
      }
      toast.success(`Ticket ${ticket.ticket_id} created!`);
      navigate(`/tickets/${ticket._id}`);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = detail === "Not authenticated"
        ? "Session expired — please log in again"
        : typeof detail === "string" ? detail : "Failed to create ticket";
      toast.error(msg);
    } finally { setLoading(false); }
  };

  const handleFiles = (e) => {
    setFiles((prev) => [...prev, ...Array.from(e.target.files || [])]);
    e.target.value = "";
  };

  const showPayment = form.chargers.some((c) => c.warranty_status === "Out of warranty");

  return (
    <div className="min-h-screen bg-gray-50" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto flex items-center gap-3">
          <button onClick={() => navigate(-1)} data-testid="back-btn"
            className="p-2 rounded-xl hover:bg-gray-100 text-gray-500 transition-colors">
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-xl font-bold text-gray-900 tracking-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>
              New Service Ticket
            </h1>
            <p className="text-xs text-gray-400 mt-0.5">Create a new charger service record</p>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto p-6">
        <form onSubmit={handleSubmit}>
          {/* Customer Details */}
          <Section title="Customer Details">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <F label="Customer / Company Name *">
                <Input value={form.customer_name} onChange={setE("customer_name")} placeholder="Enter name or company" data-testid="customer-name" className="border-gray-200 focus:ring-lime-500 focus:border-lime-500" required />
              </F>
              <F label="Phone Number *">
                <Input value={form.phone} onChange={setE("phone")} placeholder="+91 XXXXX XXXXX" data-testid="customer-phone" className="border-gray-200 focus:ring-lime-500 focus:border-lime-500" required />
              </F>
              <F label="Alternate Number">
                <Input value={form.alternate_phone} onChange={setE("alternate_phone")} placeholder="+91 XXXXX XXXXX" data-testid="customer-alt-phone" className="border-gray-200 focus:ring-lime-500 focus:border-lime-500" />
              </F>
              <F label="Address *" full>
                <Textarea value={form.address} onChange={setE("address")} placeholder="Full address with city, state" data-testid="customer-address" rows={2} className="border-gray-200 focus:ring-lime-500 focus:border-lime-500" required />
              </F>
            </div>
          </Section>

          {/* Charger Details — dynamic multiple */}
          <div className="bg-white rounded-2xl border border-gray-200 p-6 mb-4 shadow-sm">
            <div className="flex items-center justify-between mb-4 pb-3 border-b border-gray-100">
              <h3 className="text-xs font-bold uppercase tracking-widest text-gray-400" style={{ fontFamily: "'Outfit', sans-serif" }}>
                Charger Details ({form.chargers.length})
              </h3>
              <button type="button" onClick={addCharger} data-testid="add-charger-btn"
                className="flex items-center gap-1.5 text-xs font-bold text-lime-700 bg-lime-50 border border-lime-200 px-3 py-1.5 rounded-xl hover:bg-lime-100 transition-colors">
                <Plus className="w-3.5 h-3.5" /> Add Charger
              </button>
            </div>

            <div className="space-y-6">
              {form.chargers.map((charger, idx) => (
                <div key={idx} className={idx > 0 ? "pt-6 border-t border-gray-100" : ""}>
                  {/* Charger card header */}
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <span className="w-7 h-7 bg-lime-500 text-white rounded-full flex items-center justify-center text-xs font-black shadow-sm shadow-lime-900/20">
                        {idx + 1}
                      </span>
                      <span className="text-sm font-bold text-gray-700">Charger #{idx + 1}</span>
                      {(charger.charger_model || charger.serial_number) && (
                        <span className="text-xs text-gray-400">
                          — {CHARGER_SEGMENTS[charger.charger_model]?.label || charger.charger_model || ""}
                          {charger.serial_number ? ` · S/N: ${charger.serial_number}` : ""}
                        </span>
                      )}
                    </div>
                    {form.chargers.length > 1 && (
                      <button type="button" onClick={() => removeCharger(idx)} data-testid={`remove-charger-${idx}`}
                        className="flex items-center gap-1 text-xs font-semibold text-red-500 hover:text-red-700 px-2 py-1 rounded-lg hover:bg-red-50 transition-colors">
                        <X className="w-3.5 h-3.5" /> Remove
                      </button>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* ── Charger type segments + model dropdown ── */}
                    <div className="md:col-span-2 space-y-3">
                      <Label className="text-xs font-bold uppercase tracking-wider text-gray-500 block">Charger Type</Label>
                      <div className="flex gap-2">
                        {Object.entries(CHARGER_SEGMENTS).map(([key, seg]) => (
                          <button key={key} type="button"
                            data-testid={`segment-${idx}-${key.split(" ")[0].toLowerCase()}`}
                            onClick={() => setForm(f => ({
                              ...f,
                              chargers: f.chargers.map((c, i) =>
                                i === idx ? { ...c, charger_model: key, voltage: "", ampere: "" } : c
                              )
                            }))}
                            className={`flex-1 py-2.5 px-2 rounded-xl text-xs font-bold border-2 transition-all duration-150 ${
                              charger.charger_model === key
                                ? "bg-lime-50 border-lime-500 text-lime-700"
                                : "bg-gray-50 border-gray-200 text-gray-500 hover:border-lime-300 hover:text-lime-600"
                            }`}
                          >
                            {seg.label}
                          </button>
                        ))}
                      </div>
                      {charger.charger_model && CHARGER_SEGMENTS[charger.charger_model] && (
                        <div>
                          <Label className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-1.5 block">Battery Cut-Off / Ampere</Label>
                          <Select
                            value={charger.voltage && charger.ampere ? `${charger.voltage}/${charger.ampere}` : "_blank_"}
                            onValueChange={(val) => {
                              const { voltage, ampere } = val === "_blank_" ? { voltage:"", ampere:"" } : parseChargerModel(val);
                              setForm(f => ({
                                ...f,
                                chargers: f.chargers.map((c, i) => i === idx ? { ...c, voltage, ampere } : c)
                              }));
                            }}
                          >
                            <SelectTrigger data-testid={`charger-model-${idx}`} className="border-gray-200 focus:ring-lime-500">
                              <SelectValue placeholder="Select voltage / ampere" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="_blank_">— Select voltage / ampere —</SelectItem>
                              {CHARGER_SEGMENTS[charger.charger_model].models.map((m) => (
                                <SelectItem key={m} value={m}>{m}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                    </div>
                    <F label="Body Type">
                      <Select value={charger.body_type} onValueChange={setC(idx, "body_type")}>
                        <SelectTrigger data-testid={`body-type-${idx}`} className="border-gray-200"><SelectValue placeholder="— Select Body Type —" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="_blank_">— Select Body Type —</SelectItem>
                          <SelectItem value="Metal body">Metal Body</SelectItem>
                          <SelectItem value="ALUMINUM body">Aluminum Body</SelectItem>
                        </SelectContent>
                      </Select>
                    </F>
                    <F label="Charger Serial Number">
                      <Input value={charger.serial_number} onChange={setCE(idx, "serial_number")} placeholder="Enter serial number" data-testid={`serial-number-${idx}`} className="border-gray-200 focus:ring-lime-500 focus:border-lime-500" />
                    </F>
                    <F label="Warranty Status">
                      <Select value={charger.warranty_status} onValueChange={setC(idx, "warranty_status")}>
                        <SelectTrigger data-testid={`warranty-status-${idx}`} className="border-gray-200"><SelectValue placeholder="— Select Warranty —" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="_blank_">— Select Warranty —</SelectItem>
                          <SelectItem value="Under warranty">Under Warranty</SelectItem>
                          <SelectItem value="Out of warranty">Out of Warranty</SelectItem>
                          <SelectItem value="Open">Open</SelectItem>
                          <SelectItem value="Damage">Damage</SelectItem>
                        </SelectContent>
                      </Select>
                    </F>
                    <div className="md:col-span-2 grid grid-cols-2 gap-4">
                      <F label="Manufacturing Month">
                        <Select value={charger.mfg_month} onValueChange={setC(idx, "mfg_month")}>
                          <SelectTrigger data-testid={`mfg-month-${idx}`} className="border-gray-200"><SelectValue placeholder="— Select Month —" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="_blank_">— Select Month —</SelectItem>
                            {MONTHS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </F>
                      <F label="Manufacturing Year">
                        <Select value={charger.mfg_year} onValueChange={setC(idx, "mfg_year")}>
                          <SelectTrigger data-testid={`mfg-year-${idx}`} className="border-gray-200"><SelectValue placeholder="— Select Year —" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="_blank_">— Select Year —</SelectItem>
                            {YEARS.map((y) => <SelectItem key={y} value={y}>{y}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </F>
                    </div>
                  </div>

                  {/* ── XP Chargers per charger ── */}
                  <div className="mt-4 pt-4 border-t border-gray-100 space-y-2">
                    <Label className="text-xs font-bold uppercase tracking-wider text-gray-500 block">XP Chargers</Label>
                    {[{v:"returned",l:"Returned to Service Centre"},{v:"new_given",l:"New Given to Customer"}].map(opt => {
                      const sel = charger.xp_charger_status === opt.v;
                      return (
                        <button key={opt.v} type="button"
                          onClick={() => setForm(f => ({
                            ...f,
                            chargers: f.chargers.map((c,i) => i===idx ? {...c, xp_charger_status: c.xp_charger_status===opt.v?"":opt.v} : c)
                          }))}
                          className={`w-full px-4 py-3 rounded-xl text-sm font-bold text-left flex items-center gap-3 border-2 transition-all ${sel&&opt.v==="returned"?"bg-orange-50 border-orange-500 text-orange-800":sel?"bg-green-50 border-green-500 text-green-800":"bg-gray-50 border-gray-200 text-gray-600"}`}
                        >
                          <span className={`w-5 h-5 rounded-full border-2 flex-shrink-0 flex items-center justify-center ${sel&&opt.v==="returned"?"border-orange-500 bg-orange-500":sel?"border-green-500 bg-green-500":"border-gray-300 bg-white"}`}>
                            {sel && <span className="w-2 h-2 bg-white rounded-full" />}
                          </span>
                          {opt.l}
                        </button>
                      );
                    })}
                  </div>

                  {/* ── Service Details per charger ── */}
                  <div className="mt-4 pt-4 border-t border-gray-100">
                    <Label className="text-xs font-bold uppercase tracking-wider text-gray-500 block mb-3">Service Details</Label>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <F label="Service Type">
                        <Select value={charger.service_type} onValueChange={setC(idx, "service_type")}>
                          <SelectTrigger className="border-gray-200"><SelectValue placeholder="— Select Type —" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="_blank_">— Select Type —</SelectItem>
                            <SelectItem value="Repair">Repair</SelectItem>
                            <SelectItem value="Replacement">Replacement</SelectItem>
                          </SelectContent>
                        </Select>
                      </F>
                      <F label="Repair Details" full>
                        <Textarea value={charger.repair_details} onChange={setCE(idx, "repair_details")} placeholder="Describe the work done by the technician..." rows={2} className="border-gray-200 focus:ring-lime-500 focus:border-lime-500" />
                      </F>
                    </div>
                  </div>
                </div>
              ))}

            </div>

            {/* Add charger button at bottom */}
            <button type="button" onClick={addCharger}
              className="mt-5 w-full flex items-center justify-center gap-2 py-2.5 border-2 border-dashed border-gray-200 rounded-xl text-sm font-semibold text-gray-500 hover:border-lime-300 hover:text-lime-700 hover:bg-lime-50/50 transition-all duration-200">
              <Plus className="w-4 h-4" /> Add Another Charger
            </button>
          </div>

          {/* Problem & Service Details */}
          <Section title="Problem & Service Details">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <F label="Received Date *">
                <Input type="date" value={form.received_date} onChange={setE("received_date")} data-testid="received-date" className="border-gray-200 focus:ring-lime-500 focus:border-lime-500" required />
              </F>
              <F label="Fault Description (Customer Complaint)" full>
                <Textarea value={form.fault_description} onChange={setE("fault_description")} placeholder="What problem did the customer report? e.g. Charger not working, heating issue..." data-testid="fault-description" rows={2} className="border-gray-200 focus:ring-lime-500 focus:border-lime-500" />
              </F>
            </div>
          </Section>

          {/* Courier Dispatch */}
          <Section title="Courier Dispatch Details">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <F label="Courier Name">
                <Input value={form.courier_name} onChange={setE("courier_name")} placeholder="Delhivery, DTDC, BlueDart..." data-testid="courier-name" className="border-gray-200 focus:ring-lime-500 focus:border-lime-500" />
              </F>
              <F label="AWB Number">
                <Input value={form.dispatch_awb} onChange={setE("dispatch_awb")} placeholder="AWB / tracking number" data-testid="dispatch-awb" className="border-gray-200 focus:ring-lime-500 focus:border-lime-500" />
              </F>
              <F label="Tracking Link" full>
                <Input value={form.tracking_link} onChange={setE("tracking_link")} placeholder="https://..." data-testid="tracking-link" className="border-gray-200 focus:ring-lime-500 focus:border-lime-500" />
              </F>
            </div>
          </Section>

          {/* Payment — Out of Warranty */}
          {showPayment && (
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 mb-4">
              <h3 className="text-xs font-bold uppercase tracking-widest text-amber-700 mb-4 pb-3 border-b border-amber-200" style={{ fontFamily: "'Outfit', sans-serif" }}>
                Payment Details (Out of Warranty)
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <F label="Total Amount (₹)">
                  <Input type="number" value={form.payment_total} onChange={setE("payment_total")} placeholder="0.00" data-testid="payment-total" min="0" step="0.01" className="border-amber-200 focus:ring-amber-500" />
                </F>
                <F label="Paid Amount (₹)">
                  <Input type="number" value={form.payment_paid} onChange={setE("payment_paid")} placeholder="0.00" data-testid="payment-paid" min="0" step="0.01" className="border-amber-200 focus:ring-amber-500" />
                </F>
                <F label="Payment Method">
                  <Select value={form.payment_method} onValueChange={set("payment_method")}>
                    <SelectTrigger data-testid="payment-method" className="border-amber-200"><SelectValue placeholder="— Select Method —" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_blank_">— Select Method —</SelectItem>
                      <SelectItem value="Cash">Cash</SelectItem>
                      <SelectItem value="UPI">UPI</SelectItem>
                      <SelectItem value="Card">Card</SelectItem>
                      <SelectItem value="Bank Transfer">Bank Transfer</SelectItem>
                      <SelectItem value="Cheque">Cheque</SelectItem>
                      <SelectItem value="Online">Online</SelectItem>
                    </SelectContent>
                  </Select>
                </F>
                <F label="Payment Notes" full>
                  <Textarea value={form.payment_notes} onChange={setE("payment_notes")} placeholder="Invoice number, remarks..." data-testid="payment-notes" rows={2} className="border-amber-200 focus:ring-amber-500" />
                </F>
              </div>
            </div>
          )}

          {/* File Upload */}
          <div className="bg-white rounded-2xl border border-gray-200 p-6 mb-6 shadow-sm">
            <h3 className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-4 pb-3 border-b border-gray-100" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Product Images & Documents
            </h3>
            <label className="flex flex-col items-center gap-3 p-8 border-2 border-dashed border-gray-200 rounded-xl cursor-pointer hover:border-lime-400 hover:bg-lime-50/50 transition-all duration-200" data-testid="file-upload-zone">
              <Upload className="w-8 h-8 text-gray-400" />
              <div className="text-center">
                <p className="text-sm font-semibold text-gray-700">Click to upload images or documents</p>
                <p className="text-xs text-gray-400 mt-1">PNG, JPG, PDF accepted</p>
              </div>
              <input type="file" multiple accept="image/*,.pdf" onChange={handleFiles} className="hidden" data-testid="file-input" />
            </label>
            {files.length > 0 && (
              <div className="mt-3 space-y-2">
                {files.map((f, i) => (
                  <div key={i} className="flex items-center justify-between bg-gray-50 px-4 py-2 rounded-xl border border-gray-200">
                    <span className="text-sm text-gray-700 truncate">{f.name}</span>
                    <button type="button" onClick={() => setFiles((prev) => prev.filter((_, j) => j !== i))}
                      className="text-gray-400 hover:text-red-500 transition-colors ml-2 flex-shrink-0">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex gap-3 justify-end">
            <Button type="button" variant="outline" onClick={() => navigate(-1)} data-testid="cancel-btn"
              className="border-gray-200 text-gray-600 hover:bg-gray-50">
              Cancel
            </Button>
            <Button type="submit" disabled={loading} data-testid="submit-ticket-btn"
              className="bg-lime-600 hover:bg-lime-700 text-white min-w-[150px] shadow-sm shadow-lime-900/20 font-bold">
              {loading ? "Creating..." : "Create Ticket"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
